/**
 * Hono entity factory — parallel to runtime/entity-factory.ts.
 *
 * Service creation delegates to the existing runtime factory (unchanged in F2).
 * Only the controller + router swap to Hono-native implementations.
 */

import type {
  EntityDefinition,
  ModuleContext,
  BaseEntityService,
  CreateEntityServiceOptions
} from '@gzl10/nexus-sdk'
import { createEntityServiceAsync } from '../runtime/entity-factory.js'
import { createEntityController } from './controllers/entity.controller.js'
import { createEntityRouter } from './routes/entity.routes.js'
import type { HonoApp } from './types.js'
import type { EntityController, EntityService } from './controllers/types.js'

export interface EntityRuntime<T = unknown> {
  service: BaseEntityService<T>
  controller: EntityController
  router: HonoApp
}

/**
 * Create service + controller + Hono router for an entity definition.
 * If `serviceOverride` is provided (e.g. a custom service pre-registered in init),
 * it is used directly instead of creating a new generic one via the factory.
 */
export async function createEntityRuntime<T extends Record<string, unknown> = Record<string, unknown>>(
  ctx: ModuleContext,
  definition: EntityDefinition,
  options?: CreateEntityServiceOptions<T>,
  serviceOverride?: BaseEntityService<unknown>
): Promise<EntityRuntime<T>> {
  const service = serviceOverride
    ? serviceOverride as unknown as BaseEntityService<T>
    : await createEntityServiceAsync<T>(ctx, definition, options)
  const controller = createEntityController(service as unknown as EntityService, definition, ctx)
  const router = createEntityRouter(controller, definition, ctx, service as unknown as EntityService)
  return { service: service as BaseEntityService<T>, controller, router }
}
